import { useConfirm } from '@/components/Confirm';
import { Modal } from '@/components/Modal';
import {
  type CreateCustomFieldDefinitionInput,
  type CustomFieldDefinition,
  type CustomFieldKind,
  type CustomFieldScope,
  createCustomFieldDefinition,
  deactivateCustomFieldDefinition,
  listCustomFieldDefinitions,
  updateCustomFieldDefinition,
} from '@/lib/api';
import { Pencil, Plus, Trash2, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

const SCOPES: CustomFieldScope[] = ['customer', 'pet', 'booking'];
const KINDS: CustomFieldKind[] = ['text', 'number', 'date', 'boolean', 'select'];

const SCOPE_LABELS: Record<CustomFieldScope, string> = {
  customer: 'Customer',
  pet: 'Pet',
  booking: 'Booking',
};

export function CustomFieldsSettings() {
  const [defs, setDefs] = useState<CustomFieldDefinition[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<CustomFieldDefinition | null>(null);
  const { confirm, ConfirmDialog } = useConfirm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listCustomFieldDefinitions();
      const active = res.definitions.filter((d) => d.isActive === 1);
      setDefs(active);
    } catch (e) {
      setError((e as Error).message ?? 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleDeactivate(id: string) {
    const yes = await confirm({
      title: 'Deactivate this field?',
      message: 'Historical values will be preserved.',
      confirmLabel: 'Deactivate',
      danger: true,
    });
    if (!yes) return;
    try {
      await deactivateCustomFieldDefinition(id);
      await load();
    } catch (e) {
      setError((e as Error).message ?? 'Deactivate failed');
    }
  }

  const grouped: Record<CustomFieldScope, CustomFieldDefinition[]> = {
    customer: [],
    pet: [],
    booking: [],
  };
  for (const d of defs) grouped[d.scope].push(d);
  for (const s of SCOPES) grouped[s].sort((a, b) => a.position - b.position);

  return (
    <div className="space-y-6">
      <ConfirmDialog />
      <h1 className="font-heading text-2xl font-medium tracking-tight">Custom fields</h1>

      {error && (
        <div className="mb-4 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="mb-6 flex justify-end">
        <button
          type="button"
          onClick={() => {
            setEditing(null);
            setEditorOpen(true);
          }}
          className="flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 press"
        >
          <Plus className="size-3.5" strokeWidth={1.75} />
          New field
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <div className="space-y-6">
          {SCOPES.map((scope) => (
            <section key={scope} className="rounded-xl border border-border bg-surface">
              <div className="border-b border-border px-5 py-4">
                <h2 className="text-sm font-semibold">{SCOPE_LABELS[scope]} fields</h2>
              </div>
              {grouped[scope].length === 0 ? (
                <p className="px-5 py-6 text-sm text-muted-foreground">
                  No {scope} custom fields defined.
                </p>
              ) : (
                <ul className="divide-y divide-border">
                  {grouped[scope].map((d) => (
                    <li key={d.id} className="flex items-center justify-between px-5 py-3.5">
                      <div>
                        <p className="text-sm font-medium">{d.label}</p>
                        <p className="text-xs text-muted-foreground">
                          {d.kind}
                          {d.required === 1 ? ' · required' : ''}
                          {d.optionsJson
                            ? ` · ${(JSON.parse(d.optionsJson) as string[]).length} options`
                            : ''}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          aria-label={`Edit ${d.label}`}
                          onClick={() => {
                            setEditing(d);
                            setEditorOpen(true);
                          }}
                          className="rounded-md border border-border px-2 py-1.5 text-xs hover:bg-secondary press"
                        >
                          <Pencil className="size-3.5" strokeWidth={1.75} />
                        </button>
                        <button
                          type="button"
                          aria-label={`Deactivate ${d.label}`}
                          onClick={() => void handleDeactivate(d.id)}
                          className="rounded-md border border-border px-2 py-1.5 text-xs hover:bg-destructive/10 hover:text-destructive press"
                        >
                          <Trash2 className="size-3.5" strokeWidth={1.75} />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>
      )}

      {editorOpen && (
        <CustomFieldEditor
          existing={editing}
          onClose={() => {
            setEditorOpen(false);
            setEditing(null);
          }}
          onSaved={async () => {
            setEditorOpen(false);
            setEditing(null);
            await load();
          }}
        />
      )}
    </div>
  );
}

interface CustomFieldEditorProps {
  existing: CustomFieldDefinition | null;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}

function CustomFieldEditor({ existing, onClose, onSaved }: CustomFieldEditorProps) {
  const [scope, setScope] = useState<CustomFieldScope>(existing?.scope ?? 'pet');
  const [label, setLabel] = useState(existing?.label ?? '');
  const [kind, setKind] = useState<CustomFieldKind>(existing?.kind ?? 'text');
  const [required, setRequired] = useState(existing?.required === 1);
  const [position, setPosition] = useState<string>(String(existing?.position ?? 0));
  const [optionsText, setOptionsText] = useState<string>(
    existing?.optionsJson ? (JSON.parse(existing.optionsJson) as string[]).join('\n') : '',
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function handleSave() {
    setBusy(true);
    setErr(null);
    try {
      const positionInt = Number(position);
      if (!Number.isInteger(positionInt) || positionInt < 0) {
        throw new Error('position must be a non-negative integer');
      }
      if (existing) {
        await updateCustomFieldDefinition(existing.id, {
          label: label.trim(),
          required,
          position: positionInt,
        });
      } else {
        const payload: CreateCustomFieldDefinitionInput = {
          scope,
          label: label.trim(),
          kind,
          required,
          position: positionInt,
        };
        if (kind === 'select') {
          const opts = optionsText
            .split('\n')
            .map((s) => s.trim())
            .filter((s) => s.length > 0);
          if (opts.length === 0) throw new Error('select fields need at least one option');
          payload.options = opts;
        }
        await createCustomFieldDefinition(payload);
      }
      await onSaved();
    } catch (e) {
      setErr((e as Error).message ?? 'Save failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal onClose={onClose} maxWidth="max-w-md">
      <div>
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <h2 className="text-sm font-semibold">
            {existing ? 'Edit custom field' : 'New custom field'}
          </h2>
          <button type="button" aria-label="Close" onClick={onClose} className="press">
            <X className="size-4" strokeWidth={1.75} />
          </button>
        </div>

        <div className="space-y-4 px-5 py-4">
          {err && (
            <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{err}</p>
          )}

          {!existing && (
            <div>
              <label
                htmlFor="cf-scope"
                className="mb-1 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
              >
                Scope
              </label>
              <select
                id="cf-scope"
                value={scope}
                onChange={(e) => setScope(e.target.value as CustomFieldScope)}
                className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm"
              >
                {SCOPES.map((s) => (
                  <option key={s} value={s}>
                    {SCOPE_LABELS[s]}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label
              htmlFor="cf-label"
              className="mb-1 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
            >
              Label
            </label>
            <input
              id="cf-label"
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm"
              placeholder="e.g. Microchip number"
            />
          </div>

          {!existing && (
            <div>
              <label
                htmlFor="cf-kind"
                className="mb-1 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
              >
                Kind
              </label>
              <select
                id="cf-kind"
                value={kind}
                onChange={(e) => setKind(e.target.value as CustomFieldKind)}
                className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm"
              >
                {KINDS.map((k) => (
                  <option key={k} value={k}>
                    {k}
                  </option>
                ))}
              </select>
            </div>
          )}

          {!existing && kind === 'select' && (
            <div>
              <label
                htmlFor="cf-options"
                className="mb-1 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
              >
                Options (one per line)
              </label>
              <textarea
                id="cf-options"
                rows={4}
                value={optionsText}
                onChange={(e) => setOptionsText(e.target.value)}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                placeholder={'small\nmedium\nlarge'}
              />
            </div>
          )}

          <div className="flex items-center gap-2">
            <input
              id="cf-required"
              type="checkbox"
              checked={required}
              onChange={(e) => setRequired(e.target.checked)}
              className="size-4"
            />
            <label htmlFor="cf-required" className="text-sm">
              Required
            </label>
          </div>

          <div>
            <label
              htmlFor="cf-position"
              className="mb-1 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
            >
              Position
            </label>
            <input
              id="cf-position"
              type="number"
              min="0"
              value={position}
              onChange={(e) => setPosition(e.target.value)}
              className="num h-9 w-full rounded-md border border-border bg-background px-3 text-sm"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-border px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-md border border-border px-4 py-2 text-sm font-medium hover:bg-secondary press disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={busy}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 press disabled:opacity-60"
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
